import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from './Card'
import { Icon } from './Icon'
import { useAuth } from '../lib/auth'

// Carrier payments entered by hand (Sahin 2026-10-25, migration 082): FedEx and
// UPS pay claims by check. Record the check once and split it over the claims
// and disputes it pays. A claim becomes "carrier paid"; a dispute gets a
// "carrier credit found" to apply with the dispute's own Apply. Payments are
// never deleted - a wrong one is voided with a reason.

interface Allocation {
  id: number; claim_id: number | null; dispute_id: number | null; amount: string; tracking: string | null
  credit_status: 'NEW' | 'APPLIED' | 'DISMISSED' | null; dispute_status: string | null
}
interface Payment {
  payment_id: number; carrier: string; method: string; reference: string | null; amount: string
  payment_date: string; received_date: string | null; note: string | null; status: 'RECORDED' | 'VOID'
  entered_by: string; entered_at: string; voided_by: string | null; voided_at: string | null; void_reason: string | null
  allocated: string; allocations: Allocation[]
}
interface TargetClaim { claim_id: number; tracking_number: string; customer_name: string | null; claim_reference: string | null; claim_type: string | null; status: string; declared_value_usd_minor: number | null }
interface TargetDispute { dispute_id: number; shipment_number: string; invoice_number: string | null; carrier_case_ref: string | null; status: string; disputed_amount: string; dispute_category: string }
interface Line { kind: 'claim' | 'dispute'; id: number; label: string; amount: string }

// Asendia and ePost pay by credit memo - imported automatically, never entered here
const CARRIERS = ['FEDEX', 'UPS', 'DHL', 'USPS', 'APC', 'INTELCOM']
const METHOD_LABEL: Record<string, string> = { check: 'Check', ach: 'ACH', wire: 'Wire', account_credit: 'Account credit', other: 'Other' }
const money = (v: string | number) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const inputCls = 'h-8 px-2 text-[12.5px] border border-ink-200 rounded-md bg-white'
/** Today on this computer's calendar (toISOString would give tomorrow from the evening on in the US). */
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }

function RecordPaymentForm({ onDone }: { onDone: () => void }) {
  const [carrier, setCarrier] = useState('FEDEX')
  const [method, setMethod] = useState('check')
  const [reference, setReference] = useState('')
  const [amount, setAmount] = useState('')
  const [payDate, setPayDate] = useState(today())
  const [recDate, setRecDate] = useState(today())
  const [note, setNote] = useState('')
  const [lines, setLines] = useState<Line[]>([])
  const [search, setSearch] = useState('')
  const [confirm, setConfirm] = useState(false)
  const [dupMsg, setDupMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const targets = useQuery<{ claims: TargetClaim[]; disputes: TargetDispute[] }>({
    queryKey: ['payment-targets', carrier, search],
    queryFn: async () => {
      const r = await fetch(`/api/carrier-payments/targets?carrier=${carrier}&q=${encodeURIComponent(search.trim())}`)
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      return r.json()
    },
  })
  const total = Number(amount) || 0
  const allocated = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0)
  const rest = Math.round((total - allocated) * 100) / 100
  const has = (kind: string, id: number) => lines.some(l => l.kind === kind && l.id === id)
  const add = (l: Line) => { setLines(p => [...p, l]); setConfirm(false) }

  const save = useMutation({
    mutationFn: async (confirmDuplicate: boolean) => {
      const r = await fetch('/api/carrier-payments', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirm: true, confirm_duplicate: confirmDuplicate, carrier, method, reference, amount: total,
          payment_date: payDate, received_date: recDate || null, note,
          allocations: lines.map(l => ({ kind: l.kind, id: l.id, amount: Number(l.amount) })),
        }),
      })
      const j = await r.json().catch(() => null)
      if (r.status === 409 && j?.duplicate) { setDupMsg(j.error); throw new Error(j.error) }
      if (!r.ok) throw new Error(j?.error || 'Saving failed')
    },
    onSuccess: onDone,
    onError: (e: Error) => { setErr(e.message); setConfirm(false) },
  })
  const valid = total > 0 && payDate && rest >= 0 && lines.every(l => Number(l.amount) > 0)

  return (
    <div className="rounded-lg border border-royal-100 bg-royal-50 p-4 space-y-3">
      <div className="text-[13px] font-semibold text-ink-900">Record a carrier payment</div>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-[10.5px] font-semibold text-ink-500 uppercase">Carrier<br />
          <select value={carrier} onChange={e => { setCarrier(e.target.value); setLines([]); setConfirm(false); setDupMsg(null) }} className={inputCls}>
            {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-[10.5px] font-semibold text-ink-500 uppercase">Method<br />
          <select value={method} onChange={e => { setMethod(e.target.value); setConfirm(false) }} className={inputCls}>
            {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="text-[10.5px] font-semibold text-ink-500 uppercase">{method === 'check' ? 'Check no.' : 'Reference'}<br />
          <input value={reference} onChange={e => { setReference(e.target.value); setConfirm(false); setDupMsg(null) }} maxLength={100} className={`${inputCls} w-36`} />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-500 uppercase">Amount ($)<br />
          <input type="number" min={0} step="0.01" value={amount} onChange={e => { setAmount(e.target.value); setConfirm(false) }} className={`${inputCls} w-28`} />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-500 uppercase">{method === 'check' ? 'Check date' : 'Payment date'}<br />
          <input type="date" value={payDate} onChange={e => { setPayDate(e.target.value); setConfirm(false) }} className={inputCls} />
        </label>
        <label className="text-[10.5px] font-semibold text-ink-500 uppercase">Received<br />
          <input type="date" value={recDate} onChange={e => { setRecDate(e.target.value); setConfirm(false) }} className={inputCls} />
        </label>
      </div>
      <label className="block text-[10.5px] font-semibold text-ink-500 uppercase">Note<br />
        <input value={note} onChange={e => { setNote(e.target.value); setConfirm(false) }} maxLength={2000} placeholder="e.g. deposited at Chase on 10/28"
               className={`${inputCls} w-full`} />
      </label>

      <div>
        <div className="text-[10.5px] font-semibold text-ink-500 uppercase mb-1">What it pays</div>
        {lines.length > 0 && (
          <table className="w-full text-[12px] mb-2 bg-white rounded-md">
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.kind}${l.id}`} className="border-b border-ink-50">
                  <td className="py-1.5 px-2">{l.label}</td>
                  <td className="py-1.5 px-2 w-32">
                    <input type="number" min={0} step="0.01" value={l.amount}
                           onChange={e => { const v = e.target.value; setLines(p => p.map((x, j) => j === i ? { ...x, amount: v } : x)); setConfirm(false) }}
                           className={`${inputCls} w-28`} />
                  </td>
                  <td className="py-1.5 px-2 w-16 text-right">
                    <button onClick={() => { setLines(p => p.filter((_, j) => j !== i)); setConfirm(false) }} className="text-[11.5px] text-ink-500 hover:text-flame-600">remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className={`text-[12px] mb-2 ${rest < 0 ? 'text-flame-600 font-semibold' : 'text-ink-600'}`}>
          Allocated {money(allocated)} of {money(total)} · {rest < 0 ? `${money(-rest)} too much` : `${money(rest)} not allocated`}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Find a ${carrier} claim or dispute: tracking, claim / dispute number, case number`}
               className={`${inputCls} w-full mb-1.5`} />
        <div className="max-h-56 overflow-y-auto bg-white rounded-md border border-ink-100">
          {targets.isLoading ? <p className="p-2 text-[12px] text-ink-400">Loading…</p>
           : targets.error ? <p className="p-2 text-[12px] text-flame-600">{(targets.error as Error).message}</p>
           : (
            <table className="w-full text-[12px]">
              <tbody>
                {targets.data?.claims.map(t => (
                  <tr key={`c${t.claim_id}`} className="border-b border-ink-50">
                    <td className="py-1 px-2 w-24 text-ink-500">Claim #{t.claim_id}</td>
                    <td className="py-1 px-2 mono">{t.tracking_number}</td>
                    <td className="py-1 px-2 text-ink-500">{t.customer_name ?? ''} · {t.status}</td>
                    <td className="py-1 px-2 w-16 text-right">
                      {!has('claim', t.claim_id) && (
                        <button onClick={() => add({ kind: 'claim', id: t.claim_id, label: `Claim #${t.claim_id} · ${t.tracking_number}`,
                                                     amount: rest > 0 ? String(rest) : '' })}
                                className="text-[11.5px] text-royal-600 font-medium">add</button>
                      )}
                    </td>
                  </tr>
                ))}
                {targets.data?.disputes.map(t => (
                  <tr key={`d${t.dispute_id}`} className="border-b border-ink-50">
                    <td className="py-1 px-2 w-24 text-ink-500">Dispute #{t.dispute_id}</td>
                    <td className="py-1 px-2 mono">{t.shipment_number}</td>
                    <td className="py-1 px-2 text-ink-500">{money(t.disputed_amount)} · {t.status.toLowerCase()}{t.carrier_case_ref ? ` · case ${t.carrier_case_ref}` : ''}</td>
                    <td className="py-1 px-2 w-16 text-right">
                      {!has('dispute', t.dispute_id) && (
                        <button onClick={() => add({ kind: 'dispute', id: t.dispute_id, label: `Dispute #${t.dispute_id} · ${t.shipment_number}`,
                                                     amount: String(Math.min(Number(t.disputed_amount), rest > 0 ? rest : Number(t.disputed_amount))) })}
                                className="text-[11.5px] text-royal-600 font-medium">add</button>
                      )}
                    </td>
                  </tr>
                ))}
                {!targets.data?.claims.length && !targets.data?.disputes.length && (
                  <tr><td className="p-2 text-ink-400">No open {carrier} claims or disputes match. A claim not in finops yet: add it on the Claims tab first.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {err && <p className="text-[12px] text-flame-600">{err}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {!confirm ? (
          <button onClick={() => { setErr(null); setConfirm(true) }} disabled={!valid}
                  className="h-8 px-3 rounded-md text-[12.5px] font-semibold bg-royal-500 text-white hover:bg-royal-600 disabled:opacity-40">
            Save payment…
          </button>
        ) : (
          <>
            <button onClick={() => save.mutate(Boolean(dupMsg))} disabled={save.isPending}
                    className="h-8 px-3 rounded-md text-[12.5px] font-semibold bg-royal-600 text-white">
              {save.isPending ? 'Saving…' : dupMsg ? 'Record it anyway' : `Confirm: record ${money(total)} ${METHOD_LABEL[method].toLowerCase()}${reference ? ` ${reference}` : ''} dated ${payDate}`}
            </button>
            <button onClick={() => setConfirm(false)} className="text-[12px] text-ink-500">Back</button>
            <span className="text-[11.5px] text-ink-500">
              {lines.filter(l => l.kind === 'claim').length} claim(s) become carrier paid;{' '}
              {lines.filter(l => l.kind === 'dispute').length} dispute(s) get a credit to apply.
            </span>
          </>
        )}
        <button onClick={onDone} className="text-[12px] text-ink-500 ml-auto">Cancel</button>
      </div>
      {dupMsg && <p className="text-[12px] text-honey-600">{dupMsg}. Check it is not the same payment before recording it again.</p>}
    </div>
  )
}

function VoidButton({ p }: { p: Payment }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const v = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/carrier-payments/${p.payment_id}/void`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
      })
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || 'Void failed')
    },
    onSuccess: () => { setOpen(false); qc.invalidateQueries({ queryKey: ['carrier-payments'] }); qc.invalidateQueries({ queryKey: ['claims'] }); qc.invalidateQueries({ queryKey: ['disputes'] }) },
    onError: (e: Error) => setErr(e.message),
  })
  if (!open) return <button onClick={() => setOpen(true)} className="text-[11.5px] text-ink-500 hover:text-flame-600">Void…</button>
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <input value={reason} onChange={e => setReason(e.target.value)} placeholder="why?" className={`${inputCls} h-7 w-40`} />
      <button onClick={() => v.mutate()} disabled={reason.trim().length < 3 || v.isPending}
              className="h-7 px-2 rounded-md text-[11.5px] font-semibold bg-flame-500 text-white disabled:opacity-40">Confirm void</button>
      <button onClick={() => setOpen(false)} className="text-[11.5px] text-ink-500">Cancel</button>
      {err && <span className="text-[11px] text-flame-600">{err}</span>}
    </span>
  )
}

/** Carrier Recovery -> Payments */
export function CarrierPaymentsTab() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [carrier, setCarrier] = useState('')
  const qs = new URLSearchParams()
  if (carrier) qs.set('carrier', carrier)
  const { data, isLoading, error } = useQuery<{ payments: Payment[] }>({
    queryKey: ['carrier-payments', qs.toString()],
    queryFn: async () => {
      const r = await fetch(`/api/carrier-payments?${qs}`)
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      return r.json()
    },
  })
  const canRecord = can('manage_claims') || can('resolve_disputes')
  const recorded = (data?.payments ?? []).filter(p => p.status === 'RECORDED')
  const totalIn = recorded.reduce((s, p) => s + Number(p.amount), 0)
  const unallocated = recorded.reduce((s, p) => s + Number(p.amount) - Number(p.allocated), 0)

  return (
    <Card className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[12.5px] text-ink-500 flex-1 min-w-[260px]">
          Payments carriers send outside their invoices - mostly FedEx and UPS claim checks. Record each one and say
          which claims and disputes it pays.
        </p>
        <select value={carrier} onChange={e => setCarrier(e.target.value)} className={inputCls}>
          <option value="">All carriers</option>
          {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {canRecord && !adding && (
          <button onClick={() => setAdding(true)}
                  className="h-8 px-3 rounded-lg bg-flame-500 hover:bg-flame-600 text-white font-semibold text-[12.5px] inline-flex items-center gap-1.5">
            <Icon name="plus" className="w-3.5 h-3.5" /> Record payment
          </button>
        )}
      </div>
      {adding && <RecordPaymentForm onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ['carrier-payments'] }); qc.invalidateQueries({ queryKey: ['claims'] }); qc.invalidateQueries({ queryKey: ['disputes'] }) }} />}
      {data && data.payments.length >= 500 && (
        <div className="text-[12px] text-honey-600">Showing the latest 500 payments; totals cover these only. Pick a carrier to see fewer.</div>
      )}
      {data && (
        <div className="text-[12.5px] text-ink-600">
          {recorded.length} payment(s) · {money(totalIn)} received{unallocated > 0.004 ? <> · <span className="text-honey-600 font-medium">{money(unallocated)} not allocated</span></> : null}
        </div>
      )}
      {isLoading ? <p className="text-[13px] text-ink-400">Loading…</p>
       : error ? <p className="text-[13px] text-flame-600">{(error as Error).message}</p>
       : !data?.payments.length ? <p className="text-[13px] text-ink-500">No payments recorded yet.</p> : (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] font-semibold text-ink-400 uppercase border-b border-ink-100">
              <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Carrier</th><th className="py-2 pr-3">Payment</th>
              <th className="py-2 pr-3 text-right">Amount</th><th className="py-2 pr-3">Pays</th><th className="py-2 pr-3">Entered</th><th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.payments.map(p => (
              <tr key={p.payment_id} className={`border-b border-ink-50 align-top ${p.status === 'VOID' ? 'text-ink-400 line-through decoration-ink-300' : ''}`}>
                <td className="py-2 pr-3 whitespace-nowrap">{p.payment_date}{p.received_date && <div className="text-[10.5px] text-ink-400 no-underline">received {p.received_date}</div>}</td>
                <td className="py-2 pr-3">{p.carrier}</td>
                <td className="py-2 pr-3">{METHOD_LABEL[p.method] ?? p.method} {p.reference && <span className="mono">{p.reference}</span>}
                  {p.note && <div className="text-[11px] text-ink-500">{p.note}</div>}</td>
                <td className="py-2 pr-3 text-right font-semibold">{money(p.amount)}
                  {Number(p.amount) - Number(p.allocated) > 0.004 && p.status === 'RECORDED' && (
                    <div className="text-[10.5px] font-normal text-honey-600">{money(Number(p.amount) - Number(p.allocated))} not allocated</div>
                  )}</td>
                <td className="py-2 pr-3 text-[11.5px]">
                  {p.allocations.length ? p.allocations.map(a => (
                    <div key={a.id}>{a.claim_id ? `Claim #${a.claim_id}` : `Dispute #${a.dispute_id}`} <span className="mono text-ink-500">{a.tracking}</span> · {money(a.amount)}
                      {a.dispute_id != null && a.credit_status === 'NEW' && <span className="text-royal-600"> · to apply on the dispute</span>}
                      {a.dispute_id != null && a.credit_status === 'DISMISSED' && p.status === 'RECORDED' && (
                        <span className="text-honey-600" title="The dispute was decided without using this payment - check it, or void the payment and record it again"> · not used by the dispute ({a.dispute_status?.toLowerCase()})</span>
                      )}
                    </div>
                  )) : <span className="text-ink-400">—</span>}
                </td>
                <td className="py-2 pr-3 text-[11px] text-ink-500">{p.entered_by}<br />{new Date(p.entered_at).toLocaleDateString()}
                  {p.status === 'VOID' && <div className="text-flame-600 no-underline">voided by {p.voided_by}: {p.void_reason}</div>}</td>
                <td className="py-2 text-right">{p.status === 'RECORDED' && canRecord && <VoidButton p={p} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

/** In the claim panel: the payments that paid this claim. */
export function ClaimPayments({ claimId }: { claimId: number }) {
  const { data, error } = useQuery<{ payments: Payment[] }>({
    queryKey: ['carrier-payments', `claim_id=${claimId}`],
    queryFn: async () => {
      const r = await fetch(`/api/carrier-payments?claim_id=${claimId}`)
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      return r.json()
    },
  })
  if (error) return <p className="text-[11.5px] text-flame-600">Carrier payments could not be loaded: {(error as Error).message}</p>
  if (!data?.payments?.length) return null
  return (
    <div>
      <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Carrier payments</div>
      {data.payments.map(p => {
        const a = p.allocations.find(x => x.claim_id === claimId)
        return (
          <div key={p.payment_id} className={`text-[12px] py-1 ${p.status === 'VOID' ? 'text-ink-400 line-through' : 'text-ink-800'}`}>
            {money(a?.amount ?? 0)} · {METHOD_LABEL[p.method] ?? p.method} {p.reference} · {p.payment_date}
            <span className="text-ink-400"> · recorded by {p.entered_by}</span>
          </div>
        )
      })}
    </div>
  )
}
